// Base vertex schema
// Based on ADR-0005 (Base Interface Pattern for Type Hierarchies)

import { z } from 'zod';
import { DiagramPropertiesSchema, DesignStatusSchema, ValueWithUnitSchema, RgbaSchema } from './common';

/**
 * Base schema for all vertices (devices)
 * All vertex types must extend this base
 * 
 * Enforces:
 * - Unique ID for graph operations (ADR-0003: Graph-Based Structure)
 * - Type discriminator for TypeScript narrowing (ADR-0008: Type Field Dual Purpose)
 * - Diagram properties for rendering (ADR-0004: Diagram Properties Namespacing)
 * - Display name for UI and debugging
 * - User notes for documentation
 * 
 * Note: Physical properties like elevation are vertex-type-specific
 * and should be defined in the specific vertex schemas that require them.
 */
/**
 * Calculation results that can be stored on any vertex.
 * Populated by the solver after a successful calculation run.
 */
export const VertexCalcSchema = z.object({
  flow: ValueWithUnitSchema.optional(),
  pressure: ValueWithUnitSchema.optional(),
  differential_total_pressure: ValueWithUnitSchema.optional(),
  suction_total_pressure: ValueWithUnitSchema.optional(),
  discharge_total_pressure: ValueWithUnitSchema.optional(),
  total_pressure: ValueWithUnitSchema.optional(),
  // Desktop's names for the SAME two quantities on a control valve, which it
  // calls inlet/outlet rather than the pump's suction/discharge. Real converter
  // output carries them (packages/pipe-converter/fixtures/example_systems/
  // pb_cv_dP_fd.json), so the schema has to describe them or a zod parse strips
  // a valve's only persisted pressures and its readout goes blank on reopen.
  //
  // Deliberately NOT renamed to suction/discharge on import: the importer
  // leaves an existing `calc` object untouched, and rewriting field names in
  // the document would lose fidelity with what Desktop actually wrote. Readers
  // accept either pair instead.
  inlet_total_pressure: ValueWithUnitSchema.optional(),
  outlet_total_pressure: ValueWithUnitSchema.optional(),
});

export type VertexCalc = z.infer<typeof VertexCalcSchema>;

export const BaseVertexSchema = z.object({
  id: z.string(),
  type: z.string(), // Will be overridden with literal by specific types

  // Display data
  diagram: DiagramPropertiesSchema,
  name: z.string(),
  note: z.string().optional(),

  // v2: common device fields from C++ device_design_record
  design_status: DesignStatusSchema.optional(),
  custom_field_values: z.record(
    z.string(), // custom field definition UUID
    z.union([z.boolean(), z.number(), z.string()])
  ).optional(),
  layer_id: z.string().optional(),

  // v2: editable per-element color override (spec §5a). Connect-native — Desktop
  // stores none. When set it wins over the computed display color. Presentation-
  // only: stripped from the versioned structural diff so a recolor never churns
  // history (see backend model-diff stripCalc/stripColor).
  color: RgbaSchema.optional(),

  // Solver calculation results
  calc: VertexCalcSchema.optional(),
});

export type BaseVertex = z.infer<typeof BaseVertexSchema>;

